import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Zap,
  Brain,
  Globe,
  Shield,
  Clock,
  ArrowRight,
  Check,
  Menu,
  X,
  Users,
  MessageSquare,
  Workflow,
  BarChart3,
  Lock,
  Headphones,
  Twitter,
  Linkedin,
  Youtube,
  LogOut,
} from 'lucide-react';
import {
  Hero3D,
  EmployeeShowcase,
  PricingSection,
  Testimonials,
  DashboardStatsPreview,
  ChatInterfacePreview,
  TaskAutomationPreview,
  BrainAIPreview,
  IntegrationsPreview,
  AlwaysOnPreview,
  AutomatesWorkPreview,
  InfiniteKnowledgePreview,
  FAQSection,
  CompetitorComparison,
} from '../components/landing';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';
import ThemeToggle from '../components/ThemeToggle';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const features = [
  {
    icon: Brain,
    title: 'Advanced AI Brain',
    description: 'Train your AI employees with your documents, websites, and custom instructions for personalized responses.',
    color: 'from-purple-500 to-violet-600',
  },
  {
    icon: Zap,
    title: 'One-Click Tasks',
    description: '100+ pre-built task templates to get things done instantly without complex setup.',
    color: 'from-cyan-500 to-blue-600',
  },
  {
    icon: Clock,
    title: '24/7 Automation',
    description: 'Schedule tasks and let your AI employees work around the clock while you focus on growth.',
    color: 'from-green-500 to-emerald-600',
  },
  {
    icon: Globe,
    title: '100+ Languages',
    description: 'Communicate and create content in over 100 languages to reach a global audience.',
    color: 'from-orange-500 to-amber-600',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'SOC 2 compliant with end-to-end encryption keeping your data safe and secure.',
    color: 'from-red-500 to-rose-600',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Share AI employees across your team with role-based access and permissions.',
    color: 'from-pink-500 to-fuchsia-600',
  },
];


export default function Landing() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { theme } = useThemeStore();
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const isDark = theme === 'dark';

  // Scroll to top on page load/reload
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  // Get user initials for avatar
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Smooth scroll to section without changing URL
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 80; // Account for fixed navigation height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - navHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-200 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? isDark
              ? 'bg-dark-900/80 backdrop-blur-xl border-b border-white/5'
              : 'bg-white/80 backdrop-blur-xl border-b border-slate-200'
            : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-purple-500/25"
              >
                <Sparkles className="w-6 h-6 text-white" />
              </motion.div>
              <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {['AI Employees', 'Features', 'Pricing', 'Testimonials'].map((item) => (
                <motion.button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  whileHover={{ y: -2 }}
                  className={`transition-colors relative group ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-cyan-500 group-hover:w-full transition-all duration-300" />
                </motion.button>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <ThemeToggle />
              {isAuthenticated && user ? (
                <>
                  {/* User Profile Dropdown */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-2 focus:outline-none"
                    >
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-purple-500/50"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-medium">
                          {getUserInitials(user.name)}
                        </div>
                      )}
                    </button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className={`absolute right-0 mt-2 w-64 rounded-xl shadow-lg border overflow-hidden ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}
                        >
                          <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                            <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{user.email}</p>
                          </div>
                          <div className="py-1">
                            <Link
                              to="/dashboard"
                              onClick={() => setIsUserMenuOpen(false)}
                              className={`flex items-center space-x-3 px-4 py-2.5 transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                              <ArrowRight className="w-4 h-4" />
                              <span>Go to Dashboard</span>
                            </Link>
                            <button
                              onClick={handleLogout}
                              className={`w-full flex items-center space-x-3 px-4 py-2.5 transition-colors ${isDark ? 'text-red-400 hover:bg-white/5' : 'text-red-600 hover:bg-slate-50'}`}
                            >
                              <LogOut className="w-4 h-4" />
                              <span>Sign out</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`transition-colors px-4 py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Log in
                  </Link>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/register"
                      className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
                    >
                      Get Started Free
                    </Link>
                  </motion.div>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-2">
              <ThemeToggle />
              <button
                className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className={`lg:hidden backdrop-blur-xl border-b overflow-hidden ${isDark ? 'bg-dark-800/95 border-white/5' : 'bg-white/95 border-slate-200'}`}
            >
              <div className="px-4 py-6 space-y-2">
                {['AI Employees', 'Features', 'Pricing', 'Testimonials'].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      // Small delay to allow menu to close before scrolling
                      setTimeout(() => {
                        scrollToSection(item.toLowerCase().replace(' ', '-'));
                      }, 100);
                    }}
                    className={`block w-full text-left transition-colors py-3 px-2 text-lg font-medium rounded-lg ${isDark ? 'text-gray-300 hover:text-white hover:bg-white/5' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                  >
                    {item}
                  </button>
                ))}
                <div className={`pt-4 space-y-3 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  {isAuthenticated && user ? (
                    <>
                      {/* User info */}
                      <div className="flex items-center space-x-3 py-2">
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-purple-500/50"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-medium">
                            {getUserInitials(user.name)}
                          </div>
                        )}
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                          <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{user.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center justify-center space-x-2 py-2 ${isDark ? 'text-gray-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                      >
                        <ArrowRight className="w-4 h-4" />
                        <span>Go to Dashboard</span>
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                        className={`w-full flex items-center justify-center space-x-2 py-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setIsMenuOpen(false)} className={`block text-center py-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                        Log in
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setIsMenuOpen(false)}
                        className="block text-center px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium"
                      >
                        Get Started Free
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section */}
      <Hero3D />

      {/* AI Employees Section */}
      <section id="ai-employees">
        <EmployeeShowcase />
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 lg:py-32 relative">
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-dark-900 via-dark-800/50 to-dark-900' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50'}`} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <Zap className="w-4 h-4 text-cyan-400 mr-2" />
              <span className="text-sm text-cyan-400">Powerful Features</span>
            </motion.div>
            <motion.h2
              variants={fadeInUp}
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Scale
              </span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}
            >
              Our platform provides all the tools you need to automate, optimize,
              and grow your business with AI-powered efficiency.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`group relative p-6 rounded-2xl backdrop-blur-sm transition-all duration-300 ${isDark ? 'bg-dark-800/50 border border-white/5 hover:border-white/10' : 'bg-white border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'}`}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className={`relative text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {feature.title}
                </h3>
                <p className={`relative ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Dashboard Stats Preview */}
      <DashboardStatsPreview />

      {/* Chat Interface Preview */}
      <ChatInterfacePreview />

      {/* Task Automation Preview */}
      <TaskAutomationPreview />

      {/* Brain AI Preview */}
      <BrainAIPreview />

      {/* Integrations Preview */}
      <IntegrationsPreview />

      {/* Always On Preview */}
      <AlwaysOnPreview />

      {/* Automates Work Preview */}
      <AutomatesWorkPreview />

      {/* Competitor Comparison - SweetPilot vs ChatGPT, Gemini, Claude */}
      <CompetitorComparison />

      {/* Pricing Section */}
      <section id="pricing">
        <PricingSection />
      </section>

      {/* Infinite Knowledge Section */}
      <InfiniteKnowledgePreview />

      {/* Testimonials Section */}
      <section id="testimonials">
        <Testimonials />
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial rounded-full blur-3xl ${isDark ? 'from-purple-500/20 via-cyan-500/10 to-transparent' : 'from-purple-500/10 via-cyan-500/5 to-transparent'}`} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeInUp}
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              Ready to Build Your
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 bg-clip-text text-transparent">
                AI-Powered Team?
              </span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className={`text-lg mb-10 max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}
            >
              Join 50,000+ businesses using SweetPilot to automate their work and
              scale faster than ever before.
            </motion.p>
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/register"
                  className="inline-flex items-center space-x-2 px-8 py-4 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium text-lg shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
                >
                  <span>Start Your Free Trial</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>No credit card required</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Logo */}
            <div className="col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot</span>
              </Link>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                AI employees that never sleep. Build, grow, and scale your
                business 24/7.
              </p>
              <div className="flex space-x-3">
                {[
                  { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/flowai' },
                  { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/flowai' },
                  { name: 'Discord', icon: MessageSquare, href: 'https://discord.gg/flowai' },
                  { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@flowai' },
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${isDark ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10' : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'}`}
                  >
                    <span className="sr-only">{social.name}</span>
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Links */}
            {[
              {
                title: 'Product',
                links: [
                  { name: 'AI Employees', href: '/employees' },
                  { name: 'Features', href: '/features' },
                  { name: 'Pricing', href: '/pricing' },
                  { name: 'Integrations', href: '/integrations' },
                  { name: 'API', href: '/api' },
                ],
              },
              {
                title: 'Company',
                links: [
                  { name: 'About', href: '/about' },
                  { name: 'Blog', href: '/blog' },
                  { name: 'Careers', href: '/careers' },
                  { name: 'Press', href: '/press' },
                  { name: 'Contact', href: '/contact' },
                ],
              },
              {
                title: 'Resources',
                links: [
                  { name: 'Documentation', href: '/docs' },
                  { name: 'Help Center', href: '/help' },
                  { name: 'Community', href: '/community' },
                  { name: 'Templates', href: '/templates' },
                  { name: 'Roadmap', href: '/roadmap' },
                  { name: 'Status', href: '/status' },
                ],
              },
              {
                title: 'Legal',
                links: [
                  { name: 'Privacy', href: '/privacy' },
                  { name: 'Terms', href: '/terms' },
                  { name: 'Security', href: '/security' },
                  { name: 'Cookies', href: '/cookies' },
                  { name: 'GDPR', href: '/gdpr' },
                  { name: 'Refund Policy', href: '/refund-policy' },
                ],
              },
            ].map((column) => (
              <div key={column.title}>
                <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                <ul className="space-y-3">
                  {column.links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
            <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
              © 2024 SweetPilot. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                Privacy Policy
              </Link>
              <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                Terms of Service
              </Link>
              <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
